import React, { useState } from "react";
import useModuleTemplates from "../hooks/queries/useModuleTemplates";
import {
    IconButton,
    Table,
    TableBody,
    TableCell,
    Tooltip,
    TableContainer,
    TablePagination,
    TableRow,
    Typography,
    Menu,
    MenuItem,
    makeStyles,
    Grid,
    TextField,
    InputAdornment,
    Switch,
    FormControlLabel,
} from "@material-ui/core";
import EnhancedTableHead from "../../components/table/EnhancedTableHead";
import moduleAreas from "../constants/moduleAreaConstants";
import CardBase from "../../components/cards/CardBase";
import ArchiveTemplateDialog from "./dialogs/ArchiveTemplateDialog";
import { Archive, Edit, History, MoreVert, Clear as ClearIcon } from "@material-ui/icons";
import { useDispatch, useSelector } from "react-redux";
import TemplateNameCell from "./TemplateNameCell";
import { Link, useRouteMatch } from "react-router-dom";
import { useAuth } from "../../contexts/authContext";
import TemplateVersionHistoryDialog from "./riskassessments/dialogs/TemplateVersionHistoryDialog";
import { setTablePage } from "../../redux/actions/tablePageActions";
import { tablePage } from "../constants/tablePageConstants";
import { sortOrderName } from "../constants/sortOrder";

const useStyles = makeStyles((theme) => ({
    menuItem: {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1),
        display: "flex",
        gap: theme.spacing(1),
        fontSize: "0.875rem",
    },
    searchTextField: {
        marginBottom: theme.spacing(1),
        marginTop: theme.spacing(1),
        width: "100%",
        '& .clearIcon': { visibility: 'hidden' },
        '&:hover .clearIcon': { visibility: 'visible' },
    },
    endAdornment: { paddingRight: theme.spacing(1) },
    tooltip: {
        margin: theme.spacing(1),
        backgroundColor: 'white',
        border: '1px solid #000',
        color: theme.palette.common.black,
        fontSize: '0.9em',
        borderRadius: 0,
        padding: theme.spacing(1),
    },
    iconBtn: {},
    clearIcon: {},
}));

const headCells = [
    { id: "name", label: "Name", isSortable: true },
    { id: "incidentType", label: "Incident Type", isSortable: true },
    { id: "site", label: "Site", isSortable: false },
    { id: "actions", label: "Actions" },
];

const tableKey = tablePage.INCIDENTS_ADMIN_TEMPLATES_TABLE;

function IncidentsAdminTemplatesTableCard() {
    const classes = useStyles();
    const dispatch = useDispatch();
    const { selectedSite, activeAppId, appSiteIds, selectedAccount, sites, rootAccounts } = useSelector((state) => state.account);
    const { incidentsAdminTemplatesTable } = useSelector((state) => state.tablePage);
    const currentAccountId = selectedAccount?.account?.externalId;
    const siteExternalIds = selectedSite ? [selectedSite.externalId] : appSiteIds[activeAppId];

    const [archiveTemplate, setArchiveTemplate] = useState(null);
    const [filterTemplateNameText, setFilterTemplateNameText] = useState("");

    const match = useRouteMatch();
    const { path } = match;
    const { isWorknestUser } = useAuth();

    const [showArchived, setShowArchived] = useState(false);

    const hasTopLevel = appSiteIds[activeAppId]?.includes(currentAccountId);
    const hideOptionForClientUserIfTemplateIsGlobal = (siteExternalId, isWorkNestUser) => (!siteExternalId && (isWorkNestUser || hasTopLevel)) || siteExternalId;

    const templates = useModuleTemplates({
        moduleArea: moduleAreas.INCIDENTS,
        pageNum: incidentsAdminTemplatesTable.page,
        rowsPerPage: incidentsAdminTemplatesTable.rowsPerPage,
        accountId: currentAccountId,
        siteExternalIds,
        orderByColumn: incidentsAdminTemplatesTable.sortBy || headCells[0].id,
        sortOrder: incidentsAdminTemplatesTable.orderBy || sortOrderName.ASC,
        frequency: null,
        sectorId: null,
        subjectId: null,
        templateType: null,
        filterText: filterTemplateNameText && filterTemplateNameText.length >= 4 ? filterTemplateNameText : null,
        isEnabled: !showArchived,
    });

    const handleRowsPerPageChange = (e) => {
        dispatch(
            setTablePage({
                key: tableKey,
                page: 0,
                rowsPerPage: parseInt(e.target.value),
            })
        );
    };

    const handlePageChange = (e, newPage) => {
        dispatch(
            setTablePage({
                key: tableKey,
                page: newPage,
                rowsPerPage: incidentsAdminTemplatesTable.rowsPerPage,
            })
        );
    };

    const handleSortClick = (event, sortColumn) => {
        const isAsc = incidentsAdminTemplatesTable.sortBy === sortColumn && incidentsAdminTemplatesTable.orderBy === sortOrderName.ASC;
        dispatch(
            setTablePage({
                ...incidentsAdminTemplatesTable,
                key: tableKey,
                page: 0,
                sortBy: sortColumn,
                orderBy: isAsc ? sortOrderName.DESC : sortOrderName.ASC,
            })
        );
    };

    // Live search handlers
    const handleNameSearchChange = (e) => setFilterTemplateNameText(e.target.value);
    const handleNameFilteredTextClear = () => setFilterTemplateNameText("");
    const showFilteredTextError = () => filterTemplateNameText && filterTemplateNameText.length > 0 && filterTemplateNameText.length < 4;

    const [anchorEl, setAnchorEl] = useState(null);
    const [open, setOpen] = useState(false);
    const [selectedTemplateId, setSelectedTemplateId] = useState();
    const [versionDialogOpen, setVersionDialogOpen] = useState(false);

    function handleCloseDialog() {
        setVersionDialogOpen(false);
    }

    function handleOpenVersionDialog(id) {
        setSelectedTemplateId(id);
        setVersionDialogOpen(true);
        setOpen(false);
    }

    const handleClick = (event, id) => {
        setAnchorEl(event.currentTarget);
        setOpen(id);
    };

    const handleClose = () => {
        setAnchorEl(null);
        setOpen(false);
    };

    const tableContent = () => {
        if (templates.data?.results?.length > 0) {
            return templates.data.results.map((t) => (
                <TableRow key={t.id}>
                    <TableCell>
                        <TemplateNameCell name={t.name} isWorkNest={!t.siteExternalId} />
                    </TableCell>
                    <TableCell>{t.incidentTypeDescription}</TableCell>
                    <TableCell>{rootAccounts[0]?.account.externalId === t.siteExternalId ? "All" : sites[t?.siteExternalId]?.name ?? "All"}</TableCell>
                    <TableCell>
                        <Tooltip title="More">
                            <IconButton
                                aria-label="more"
                                aria-controls="long-menu"
                                aria-haspopup="true"
                                onClick={(e) => handleClick(e, t.id)}
                            >
                                <MoreVert />
                            </IconButton>
                        </Tooltip>
                        <Menu
                            id="long-menu"
                            anchorEl={anchorEl}
                            keepMounted
                            open={open === t.id}
                            onClose={handleClose}
                        >
                            <MenuItem
                                className={classes.menuItem}
                                component={Link}
                                to={isWorknestUser || t.siteExternalId ? `${path}/templatebuilder/edit/${t.templateVersionId}` : `${path}/templatebuilder/edit/${t.templateVersionId}/true`}
                            >
                                <Edit />
                                {isWorknestUser || t.siteExternalId ? "Edit Template" : "Duplicate and Edit Template"}
                            </MenuItem>
                            {(isWorknestUser || t.siteExternalId) && (
                                <MenuItem
                                    className={classes.menuItem}
                                    component={Link}
                                    to={`${path}/templatebuilder/edit/${t.templateVersionId}/true`}
                                >
                                    <Edit />
                                    Duplicate and Edit Template
                                </MenuItem>
                            )}
                            {hideOptionForClientUserIfTemplateIsGlobal(t.siteExternalId, isWorknestUser) &&
                                <MenuItem
                                    className={classes.menuItem}
                                    onClick={() => { setOpen(false); setArchiveTemplate(t) }}>
                                    <Archive />
                                    {t.isEnabled ? 'Archive' : 'Restore'} Template
                                </MenuItem>
                            }
                            <MenuItem
                                className={classes.menuItem}
                                onClick={() => handleOpenVersionDialog(t.id)}
                            >
                                <History />
                                Version History
                            </MenuItem>
                        </Menu>
                    </TableCell>
                </TableRow>
            ));
        }

        return (
            <TableRow>
                <TableCell colSpan={headCells.length}>
                    <Typography>No templates have been added.</Typography>
                </TableCell>
            </TableRow>
        );
    };

    return (
        <>
            <TemplateVersionHistoryDialog
                onClose={handleCloseDialog}
                open={versionDialogOpen}
                templateId={selectedTemplateId}
            />
            <CardBase
                title="Incident Templates"
                description="A list of all available incident templates."
                isLoading={templates.isLoading}
                isFetching={templates.isFetching}
                error={templates.error}
            >
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6} xl={3}>
                        <TextField
                            className={classes.searchTextField}
                            label="Search Template Name"
                            variant="outlined"
                            size="small"
                            value={filterTemplateNameText || ""}
                            onChange={handleNameSearchChange}
                            helperText='Please enter at least4 character to search on'
                            error={showFilteredTextError()}
                            FormHelperTextProps={{ hidden: !showFilteredTextError() }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        {filterTemplateNameText && (
                                            <Tooltip title="Clear" placement="bottom-end" classes={{ tooltip: classes.tooltip }}>
                                                <IconButton className={classes.iconBtn}>
                                                    <ClearIcon className={`clearIcon ${classes.clearIcon}`} onClick={handleNameFilteredTextClear} />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </InputAdornment>
                                ),
                                className: classes.endAdornment,
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={6} xl={3}>
                        <FormControlLabel
                            control={<Switch checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />}
                            label="Show Archived"
                        />
                    </Grid>
                </Grid>
                <TableContainer>
                    <Table aria-label="Incidents">
                        <EnhancedTableHead
                            headCells={headCells}
                            sortColumn={incidentsAdminTemplatesTable.sortBy}
                            sortDirection={incidentsAdminTemplatesTable.orderBy || sortOrderName.DESC}
                            onRequestSort={handleSortClick}
                        />
                        <TableBody>{tableContent()}</TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={templates.data ? templates.data.totalCount : 0}
                    rowsPerPage={incidentsAdminTemplatesTable.rowsPerPage}
                    page={incidentsAdminTemplatesTable.page}
                    onPageChange={handlePageChange}
                    onRowsPerPageChange={handleRowsPerPageChange}
                />
                <ArchiveTemplateDialog
                    open={!!archiveTemplate}
                    onCancel={() => setArchiveTemplate(null)}
                    archiveTemplate={archiveTemplate}
                    applicationArea={moduleAreas.INCIDENTS}
                    page={incidentsAdminTemplatesTable.page}
                    rows={incidentsAdminTemplatesTable.rowsPerPage}
                    accountId={currentAccountId}
                    siteExternalIds={siteExternalIds}
                />
            </CardBase>
        </>
    );
}

export default IncidentsAdminTemplatesTableCard;
